// Daily Computing Power & Document Upload Quota Manager
// Modeled like Claude AI usage limits, allocating a daily computing capacity for PDF, DOC, pictures, and screenshots.
// Reduced from 25 MB to 8 MB to protect compute resources and significantly reduce AI processing costs.

import { safeLocalStorage } from "./safeStorage";

export const DAILY_COMPUTE_LIMIT_BYTES = 8 * 1024 * 1024; // 8 MB allocated per day (cost-reduced from 25 MB)
export const STORAGE_KEY_DAILY_COMPUTE = "catchai_daily_compute_v3";
export const LEGACY_STORAGE_KEY_DAILY_COMPUTE = "veritext_daily_compute_v3";

export interface DailyComputeRecord {
  dateStr: string; // YYYY-MM-DD
  usedBytes: number;
  uploadCount: number;
  breakdown: {
    pdfBytes: number;
    imageBytes: number;
    docBytes: number;
    screenshotBytes: number;
  };
}

// Get current date string formatted as YYYY-MM-DD in local time
export function getTodayDateString(): string {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

// Calculate milliseconds until next day midnight (00:00:00)
export function getMsUntilNextDayMidnight(): number {
  const now = new Date();
  const tomorrow = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1, 0, 0, 0, 0);
  return Math.max(0, tomorrow.getTime() - now.getTime());
}

// Format milliseconds remaining into human-friendly "Xh Ym"
export function formatTimeUntilReset(ms: number): string {
  const totalMinutes = Math.floor(ms / (1000 * 60));
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours <= 0 && minutes <= 0) return "< 1 min";
  if (hours <= 0) return `${minutes}m`;
  return `${hours}h ${minutes}m`;
}

// Format bytes into readable MB / KB
export function formatBytes(bytes: number, decimals: number = 1): string {
  if (bytes <= 0) return "0 MB";
  if (bytes < 1024 * 1024) {
    const kb = (bytes / 1024).toFixed(decimals);
    return `${kb} KB`;
  }
  const mb = (bytes / (1024 * 1024)).toFixed(decimals);
  return `${mb} MB`;
}

// Retrieve or initialize daily compute record
export function getStoredDailyCompute(): DailyComputeRecord {
  const today = getTodayDateString();
  try {
    const raw = safeLocalStorage.getItem(STORAGE_KEY_DAILY_COMPUTE) || safeLocalStorage.getItem(LEGACY_STORAGE_KEY_DAILY_COMPUTE);
    if (raw) {
      const parsed: DailyComputeRecord = JSON.parse(raw);
      // If it's a new day, auto-reset quota to fresh allocation!
      if (parsed.dateStr === today) {
        return parsed;
      }
    }
  } catch {
    // Fall back to clean daily quota
  }

  const fresh: DailyComputeRecord = {
    dateStr: today,
    usedBytes: 0,
    uploadCount: 0,
    breakdown: {
      pdfBytes: 0,
      imageBytes: 0,
      docBytes: 0,
      screenshotBytes: 0
    }
  };
  saveStoredDailyCompute(fresh);
  return fresh;
}

function saveStoredDailyCompute(record: DailyComputeRecord): void {
  try {
    safeLocalStorage.setItem(STORAGE_KEY_DAILY_COMPUTE, JSON.stringify(record));
  } catch {
    // Ignore storage quota warnings in restricted contexts
  }
}

// Calculate complete quota status
export function getDailyComputeQuotaInfo(): {
  usedBytes: number;
  totalBytes: number;
  remainingBytes: number;
  percentUsed: number;
  formattedUsed: string;
  formattedTotal: string;
  formattedRemaining: string;
  resetCountdown: string;
  isExceeded: boolean;
  uploadCount: number;
} {
  const record = getStoredDailyCompute();
  const totalBytes = DAILY_COMPUTE_LIMIT_BYTES;
  const remainingBytes = Math.max(0, totalBytes - record.usedBytes);
  const percentUsed = Math.min(100, Math.round((record.usedBytes / totalBytes) * 100));
  const isExceeded = record.usedBytes >= totalBytes;
  const msUntilReset = getMsUntilNextDayMidnight();

  return {
    usedBytes: record.usedBytes,
    totalBytes,
    remainingBytes,
    percentUsed,
    formattedUsed: formatBytes(record.usedBytes),
    formattedTotal: formatBytes(totalBytes, 0),
    formattedRemaining: formatBytes(remainingBytes),
    resetCountdown: formatTimeUntilReset(msUntilReset),
    isExceeded,
    uploadCount: record.uploadCount
  };
}

// Check if a specific file or image payload can be processed today
export function checkDailyComputeLimit(fileSizeBytes: number): {
  allowed: boolean;
  reason?: string;
  remainingBytes: number;
  formattedRemaining: string;
  resetCountdown: string;
} {
  const info = getDailyComputeQuotaInfo();

  if (info.isExceeded || info.remainingBytes <= 0) {
    return {
      allowed: false,
      reason: `Daily document computing power limit reached. Your computing allowance refreshes tomorrow in ${info.resetCountdown}.`,
      remainingBytes: 0,
      formattedRemaining: info.formattedRemaining,
      resetCountdown: info.resetCountdown
    };
  }

  if (fileSizeBytes > info.remainingBytes) {
    return {
      allowed: false,
      reason: `This document exceeds your remaining daily computing power. Your computing allowance refreshes tomorrow in ${info.resetCountdown}.`,
      remainingBytes: info.remainingBytes,
      formattedRemaining: info.formattedRemaining,
      resetCountdown: info.resetCountdown
    };
  }

  return {
    allowed: true,
    remainingBytes: info.remainingBytes,
    formattedRemaining: info.formattedRemaining,
    resetCountdown: info.resetCountdown
  };
}

// Record bytes consumed by an upload or OCR operation
export function recordDailyComputeUsage(
  bytes: number,
  category: "pdf" | "image" | "doc" | "screenshot" = "image"
): DailyComputeRecord {
  const record = getStoredDailyCompute();
  const validBytes = Math.max(0, bytes);
  record.usedBytes += validBytes;
  record.uploadCount += 1;

  if (category === "pdf") {
    record.breakdown.pdfBytes += validBytes;
  } else if (category === "doc") {
    record.breakdown.docBytes += validBytes;
  } else if (category === "screenshot") {
    record.breakdown.screenshotBytes += validBytes;
  } else {
    record.breakdown.imageBytes += validBytes;
  }

  saveStoredDailyCompute(record);
  return record;
}

// Approximate size of a base64 string in bytes
export function estimateBase64SizeBytes(base64String: string): number {
  if (!base64String) return 0;
  const cleaned = base64String.replace(/^data:[^;]+;base64,/, "");
  return Math.ceil((cleaned.length * 3) / 4);
}

// Reset daily compute/upload record to 0
export function resetDailyComputeQuota(): DailyComputeRecord {
  const fresh: DailyComputeRecord = {
    dateStr: getTodayDateString(),
    usedBytes: 0,
    uploadCount: 0,
    breakdown: {
      pdfBytes: 0,
      imageBytes: 0,
      docBytes: 0,
      screenshotBytes: 0
    }
  };
  saveStoredDailyCompute(fresh);
  return fresh;
}

